import { Injectable } from '@angular/core';
import { ScheduleFrequency } from 'src/app/enums/email.enum';
import { DateTimeValidationService } from 'src/app/helpers/datetime/datetime-validation.service';
import { SnackBarService } from 'src/app/ui-services/snackbar/snackBar.service';

/**
 * State of validation for schedule email component.
 */
export interface ValidationState {
  isValid: boolean;
  shouldDisableSchedule: boolean;
  shouldShowNotification: boolean;
  shouldProceedAnyway?: boolean; // For recurrent frequencies: send now and schedule future
  errorMessage?: string;
  warningMessage?: string;
}

/**
 * Service responsible for coordinating validation and notifications for schedule email.
 * Follows Single Responsibility Principle by focusing on validation coordination.
 */
@Injectable({
  providedIn: 'root'
})
export class ScheduleEmailValidationService {
  private hasShownPastDateWarning: boolean = false;

  constructor(private dateTimeValidationService: DateTimeValidationService) {}

  /**
   * Determines if validation should be performed based on frequency.
   * Validation is only needed for recurrent frequencies (Daily, Weekly, Monthly).
   * 
   * @param frequency - The schedule frequency
   * @returns True if validation should be performed (for recurrent frequencies)
   */
  shouldValidate(frequency: ScheduleFrequency): boolean {
    return frequency === ScheduleFrequency.Daily ||
           frequency === ScheduleFrequency.Weekly ||
           frequency === ScheduleFrequency.Monthly;
  }

  /**
   * Validates the schedule and manages notification state.
   * 
   * @param date - The schedule date
   * @param timezone - The timezone string
   * @param frequency - The schedule frequency
   * @param snackBarService - Service for showing notifications
   * @returns ValidationState with validation results
   */
  validateAndNotify(
    date: Date,
    timezone: string,
    frequency: ScheduleFrequency,
    snackBarService: SnackBarService
  ): ValidationState {
    if (!this.shouldValidate(frequency)) {
      return {
        isValid: true,
        shouldDisableSchedule: false,
        shouldShowNotification: false,
        shouldProceedAnyway: false
      };
    }

    const validationResult = this.dateTimeValidationService.validateOneTimeSchedule(date, timezone);
    // Note: validateOneTimeSchedule is used for all validations (recurrent and one-time)
    // The method name is kept for backward compatibility but works for all frequencies
    const isPast = !validationResult.isValid;

    // For recurrent frequencies, always allow proceeding
    // If datetime is in the past, enable sendNow flow (send email now and schedule for future)
    const shouldProceedAnyway = isPast && this.shouldValidate(frequency);
    const shouldShowNotification = isPast && !this.hasShownPastDateWarning;
    
    const warningMessage = 'The selected start time is in the past. The email will be sent immediately, and the recurring schedule will continue from the next occurrence.';
    
    // Only show warning messages, no error messages
    if (shouldShowNotification && shouldProceedAnyway) {
      //not showing warning message for now
      //snackBarService.showWarningSnackBar(warningMessage,6000);
      this.hasShownPastDateWarning = true;
    } else if (!isPast) {
      this.hasShownPastDateWarning = false;
    }

    // Always return valid for recurrent frequencies - validation only determines sendNow flow
    return {
      isValid: true, // Always allow proceeding for recurrent frequencies
      shouldDisableSchedule: false, // Never disable for recurrent frequencies
      shouldShowNotification: shouldShowNotification,
      shouldProceedAnyway: shouldProceedAnyway, // This becomes the sendNow flag
      warningMessage: shouldProceedAnyway ? warningMessage : undefined
    };
  }

  /**
   * Resets the warning state (useful for testing or when component is destroyed).
   */
  resetWarningState(): void {
    this.hasShownPastDateWarning = false;
  }
}

